import TripExport from '@koob/trip-export';
import { actingOrganizationOf } from '@koob/utils';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-async';
import {
  getTrip,
  getTripAccommodationLabels,
  getTripDiaryExperience,
} from '../../../api/trip';
import { me } from '../../../api/users';
import { getCatalogCountries } from '../../../api/countries';
import { getExperience } from '../../../api/experiences';
import { useTranslation } from 'react-i18next';
import KoobLoadingOverlay from '@koob/ui/src/components/KoobLoadingOverlay';
import { useDocumentTitle } from '@koob/ui';
import { getHotel } from '../../../api/hotels';
import { getLanguages } from '../../../api/languages';
import { decodeId } from '../../../utils';
import { getTravelDiarySettings } from 'api/travelDiarySettings';
import { AdminBookingStateEnum } from '@koob/enums';
import { getPricingSummary } from 'api/pricingSummaries';
import { getOrganizationTheme } from 'api/organizationThemes';
import { getTravelDiaryContents } from 'api/travelDiaryContents';

export default function ExportTrip() {
  const { tripId } = useParams();
  const { t } = useTranslation('trips');

  const [searchParams] = useSearchParams();

  const [experiences, setExperiences] = useState([]);
  const [experiencesLoading, setExperiencesLoading] = useState(false);

  const [hotels, setHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [accommodationLabels, setAccommodationLabels] = useState();

  const [langSelected, setLangSelected] = useState(null);
  const [showLanguageModal, setShowLanguageModal] = useState(true);

  const { data: tripData, isLoading: isLoadingTrip } = useAsync({
    promiseFn: getTrip,
    tripId,
    versionId: searchParams.get('version'),
    // Products we cannot fetch are named from the trip itself, in this locale.
    locale: langSelected?.value,
    watch: langSelected?.value,
  });
  const trip = tripData?.data;
  useDocumentTitle(trip?.name);

  const { data: pricingData } = useAsync({
    promiseFn: getPricingSummary,
    resourceType: 'trip',
    resourceId: tripId,
    watch: tripId,
  });

  const { data: languagesData } = useAsync({
    promiseFn: getLanguages,
  });
  const locales = languagesData?.data?.map(({ name, description }) => ({
    value: name,
    label: name.toUpperCase(),
    alpha2: description.toLowerCase(),
  }));

  const { data: userData, isLoading: isLoadingUser } = useAsync({
    promiseFn: me,
  });
  const user = userData?.data;
  const tourOperator = actingOrganizationOf(user);

  const { data: countriesData, isLoading: isLoadingCountries } = useAsync({
    promiseFn: getCatalogCountries,
  });
  const countries = countriesData?.data?.filter(c =>
    trip?.countries?.find(country => country.id === c.id),
  );

  const destinationIds = countries?.map(({ id }) => id) ?? [];
  const { data: contentsData } = useAsync({
    promiseFn: getTravelDiaryContents,
    filters: { destinations: destinationIds, locale: langSelected?.value },
    skip: destinationIds.length === 0,
    watch: `${destinationIds.join(',')}|${langSelected?.value ?? ''}`,
  });

  // KD-3788: a product the DMC stopped distributing to us stays in a trip we
  // already booked — it was sold. The regular endpoint 403s on it, so its
  // descriptive content is read from the trip instead. A draft keeps such a
  // product masked and flagged.
  // KD-3825: the trip is named so its multi-destination partner products,
  // distributed to us by nobody, are served too.
  const isBooked = trip?.draft === false;
  const getTripExperience = useCallback(
    ({ experienceId, locale }) =>
      getExperience({ experienceId, locale, tripId }),
    [tripId],
  );
  const getBookedTripExperience = useCallback(
    async ({ experienceId, locale }) => {
      try {
        return await getExperience({ experienceId, locale, tripId });
      } catch {
        return getTripDiaryExperience({ tripId, experienceId, locale });
      }
    },
    [tripId],
  );
  const getDiaryExperience = isBooked
    ? getBookedTripExperience
    : getTripExperience;

  // Products the DMC does not distribute to us: never fetched from the regular
  // endpoints, which would 403.
  const inaccessibleExperienceIds = new Set(
    trip?.tripDays
      ?.filter(day => day.experienceId && day.experienceIsAccessible === false)
      ?.map(day => day.experienceId) ?? [],
  );
  const inaccessibleHotelIds = new Set(
    trip?.tripDays
      ?.filter(day => day.hotelId && day.hotelIsAccessible === false)
      ?.map(day => day.hotelId) ?? [],
  );

  const experienceIds = [
    ...new Set(trip?.tripDays?.map(day => day.experienceId)?.filter(Boolean)),
  ];
  const hotelIds = [
    ...new Set(trip?.tripDays?.map(day => day.hotelId)?.filter(Boolean)),
  ];

  useEffect(() => {
    if (experienceIds && experienceIds.length > 0) {
      setExperiencesLoading(true);

      const promises = experienceIds.map(async experienceId => {
        if (inaccessibleExperienceIds.has(experienceId)) {
          const day = trip?.tripDays?.find(
            d => d.experienceId === experienceId,
          );
          const masked = { ...(day?.experience ?? {}), isAccessible: false };
          if (!isBooked) {
            return masked;
          }

          try {
            const experience = await getTripDiaryExperience({
              tripId,
              experienceId,
              locale: langSelected?.value,
            });
            return experience.data;
          } catch {
            return masked;
          }
        }

        try {
          const experience = await getDiaryExperience({
            experienceId,
            locale: langSelected?.value,
          });
          return experience.data;
        } catch {
          // A single unfetchable product must never leave the export stuck on
          // its loader: Promise.all would reject and the `then` below — the
          // only thing that clears `experiencesLoading` — would never run.
          return { id: experienceId, isAccessible: false };
        }
      });

      Promise.all(promises).then(res => {
        setExperiences(res);
        setExperiencesLoading(false);
      });
    }

    if (hotelIds && hotelIds.length > 0) {
      setHotelsLoading(true);

      const promises = hotelIds.map(async hotelId => {
        if (inaccessibleHotelIds.has(hotelId)) {
          const day = trip?.tripDays?.find(d => d.hotelId === hotelId);
          return {
            ...(day?.hotel ?? {}),
            id: decodeId(hotelId).toString(),
            isAccessible: false,
          };
        }

        try {
          const hotel = await getHotel({
            hotelId,
            locale: langSelected?.value,
          });
          return {
            ...hotel.data,
            id: decodeId(hotelId).toString(),
          };
        } catch {
          return { id: decodeId(hotelId).toString(), isAccessible: false };
        }
      });

      Promise.all(promises).then(res => {
        setHotels(res);
        setHotelsLoading(false);
      });
    }
    // Keyed on the trip, which is refetched whenever the language changes, so
    // the unfetchable products are read from the trip in the new locale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip]);

  useEffect(() => {
    getTripAccommodationLabels({ tripId, locale: langSelected?.value })
      .then(res => setAccommodationLabels(res?.data))
      .catch(() => setAccommodationLabels(undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, langSelected]);

  const images = experiences?.reduce((acc, experience) => {
    if (experience.pictures) {
      experience.pictures.forEach(image => {
        acc.push(image);
      });
    }
    return acc;
  }, []);

  const loading =
    isLoadingTrip ||
    isLoadingUser ||
    isLoadingCountries ||
    experiencesLoading ||
    hotelsLoading;

  // day.options / globalOptions only expose the experience/option id, not the
  // per-instance extraCompositionId, so two extras can share the same id
  // (one cancelled, one not). Only hide an option when every booked extra
  // with that id is cancelled — if any instance is still active, keep it.
  const { bookedExtraIds, activeExtraIds } = useMemo(() => {
    const bookedExtraIds = new Set();
    const activeExtraIds = new Set();
    trip?.bookings?.forEach(booking =>
      booking?.experiencesBooked?.forEach(experienceBooked =>
        experienceBooked?.extras?.forEach(extra => {
          bookedExtraIds.add(extra.id);
          if (extra.state !== AdminBookingStateEnum.CANCELLED) {
            activeExtraIds.add(extra.id);
          }
        }),
      ),
    );
    return { bookedExtraIds, activeExtraIds };
  }, [trip]);

  const filteredTrip = useMemo(() => {
    if (!trip) return trip;
    const isCancelledOption = option => {
      const optionId = option.optionId ?? option.id;
      return bookedExtraIds.has(optionId) && !activeExtraIds.has(optionId);
    };
    return {
      ...trip,
      globalOptions: trip.globalOptions?.filter(
        option => !isCancelledOption(option),
      ),
      tripDays: trip.tripDays?.map(day => ({
        ...day,
        options: day.options?.filter(option => !isCancelledOption(option)),
      })),
    };
  }, [trip, bookedExtraIds, activeExtraIds]);

  return (
    <>
      {loading ? (
        <KoobLoadingOverlay
          title={t('export.loading.title')}
          description={t('export.loading.description')}
        />
      ) : (
        <TripExport
          trip={filteredTrip}
          countries={countries}
          tourOperator={tourOperator}
          images={images}
          experiences={experiences}
          hotels={hotels}
          accommodationLabels={accommodationLabels}
          locales={locales}
          getTravelDiarySettings={getTravelDiarySettings}
          getExperience={getDiaryExperience}
          langSelected={langSelected}
          setLangSelected={setLangSelected}
          showLanguageModal={showLanguageModal}
          setShowLanguageModal={setShowLanguageModal}
          shareUrl={
            trip?.shareToken
              ? `${window.location.origin}/travel-diary/${trip.shareToken}?locale=${langSelected?.value}`
              : undefined
          }
          shareDisabled={!isBooked && trip?.inaccessibleProductCount > 0}
          pricingSummary={pricingData?.data}
          travelDiaryContents={contentsData?.data ?? []}
          getOrganizationTheme={getOrganizationTheme}
          previewControls
        />
      )}
    </>
  );
}
